<template>
  <div class="newsrc-catalog-page">
    <header class="newsrc-catalog-page__toolbar">
      <div class="newsrc-catalog-page__toolbar-inner">
        <h1 class="newsrc-catalog-page__title">{{ $t("productsLabel") }}</h1>
        <div class="newsrc-catalog-page__actions">
          <button type="button" class="newsrc-catalog-page__btn" @click="GetProducts()">
            <i class="fa fa-sync me-1" aria-hidden="true" />
            {{ $t("btn_refresh") }}
          </button>
        </div>
      </div>
    </header>

    <div
      v-loading="loading"
      class="newsrc-catalog-page__body"
      element-loading-custom-class="newsrc-catalog-loading"
      :element-loading-background="'var(--agent-loading-overlay-bg)'"
      :element-loading-text="$t('catalogLoadingHint')"
    >
      <ProductCardGrid
        :items="data"
        :selected-product-id="selectedProductId"
        @select-product="OnSelectProduct"
      />
    </div>

    <CatalogInsuredDialogs
      v-model:person-visible="personDialogVisible"
      v-model:company-visible="companyDialogVisible"
      :selected-product="selectedProduct"
    />
  </div>
</template>

<script>
import CatalogInsuredDialogs from "./CatalogInsuredDialogs.vue";
import ProductCardGrid from "./components/ProductCardGrid.vue";
import MedicalAgent from "../Source/medicalAgent";
import utils from "@/project/Source/utils.js";
import { getCatalogMockProducts } from "./catalogMockData.js";
import { isProductLegalEntity } from "./catalogProductParty.js";
import { stashSelectedCatalogProduct } from "./catalogSelectedProduct.js";

/** Append demo cards after Medical Agent API data (dedupe by productId). false — только API. */
const CATALOG_APPEND_MOCK = false;

export default {
  name: "NewsrcProductsCatalog",
  components: {
    ProductCardGrid,
    CatalogInsuredDialogs,
  },
  data() {
    return {
      loading: false,
      data: [],
      selectedProductId: null,
      selectedProduct: null,
      personDialogVisible: false,
      companyDialogVisible: false,
    };
  },
  mounted() {
    this.GetProducts();
  },
  methods: {
    mergeCatalogRows(mockRows, apiRows) {
      const seen = new Set();
      const out = [];
      for (const r of [...mockRows, ...apiRows]) {
        const id = r && r.productId;
        if (id == null || seen.has(id)) continue;
        seen.add(id);
        out.push(r);
      }
      return out;
    },
    GetProducts() {
      this.loading = true;
      const payload = {
        requestCorrelationId: crypto.randomUUID(),
      };
      MedicalAgent.GetProducts(payload)
        .then((response) => {
          const apiRows = response.data.data || [];
          this.data = CATALOG_APPEND_MOCK ? this.mergeCatalogRows(getCatalogMockProducts(), apiRows) : apiRows;
          this.loading = false;
        })
        .catch((err) => {
          this.data = CATALOG_APPEND_MOCK ? getCatalogMockProducts() : [];
          this.loading = false;
          if (err?.response?.status === 401) {
            this.$router.push("/signIn/catalog");
          }
        });
    },
    OnSelectProduct(row) {
      if (!row || row.productId == null) return;
      const name = row.prodName || String(row.productId);
      const text = this.$t("catalogSelectProductConfirm", { name });
      utils.DeleteDialog(text).then((result) => {
        if (!result.isConfirmed) return;
        this.selectedProductId = row.productId;
        this.selectedProduct = row;
        if (isProductLegalEntity(row)) {
          this.companyDialogVisible = true;
          return;
        }
        stashSelectedCatalogProduct(row);
        const route = this.$router.resolve({
          name: "CatalogProductInsert",
          params: { productId: row.productId },
        });
        window.open(route.href, "_blank", "noopener,noreferrer");
      });
    },
  },
};
</script>

<style scoped>
.newsrc-catalog-page {
  min-height: calc(100vh - 5rem);
  margin: 0;
  padding: var(--agent-page-inset) var(--agent-page-inset) 2.75rem;
  box-sizing: border-box;
  background: var(--agent-page-bg);
  color: var(--agent-text);
}

.newsrc-catalog-page__toolbar {
  margin-bottom: 1.25rem;
}

.newsrc-catalog-page__toolbar-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.newsrc-catalog-page__title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--agent-text-strong);
}

.newsrc-catalog-page__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.newsrc-catalog-page__btn {
  border-radius: 10px;
  font-weight: 600;
  padding: 0.45rem 0.9rem;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.newsrc-catalog-page__body {
  min-height: 180px;
}
</style>
